import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, FileText, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { NextBestAction } from '@/components/home/NextBestAction';
import { SampleDataBanner } from '@/components/home/SampleDataBanner';
import { WeeklyBriefing } from '@/components/home/WeeklyBriefing';
import { Modal, useToast, useConfirm, ErrorState } from '@/components/ui';
import { AgentRunPanel } from '@/components/agents/AgentRunPanel';
import { ShareSnapshotButton } from '@/components/snapshot/ShareSnapshotButton';
import { signalsApi, Signal, SignalKind } from '@/lib/signalsApi';
import { agentsApi } from '@/lib/agentsApi';
import { commentsApi } from '@/lib/commentsApi';
import { sequencesApi } from '@/lib/sequencesApi';
import { formatLocaleDate } from '@/lib/date';

const KIND_KIND: Record<SignalKind, string> = {
  job_change: 'signals.kind_job_change',
  funding: 'signals.kind_funding',
  hiring_spike: 'signals.kind_hiring_spike',
  tech_install: 'signals.kind_tech_install',
  press: 'signals.kind_press',
  tdnet: 'signals.kind_tdnet',
  edinet: 'signals.kind_edinet',
  exec_announcement: 'signals.kind_exec_announcement',
};

const KIND_COLOR: Record<SignalKind, string> = {
  job_change: 'bg-amber-50 text-amber-700',
  funding: 'bg-emerald-50 text-emerald-700',
  hiring_spike: 'bg-sky-50 text-sky-700',
  tech_install: 'bg-purple-50 text-purple-700',
  press: 'bg-slate-100 text-slate-700',
  tdnet: 'bg-rose-50 text-rose-700',
  edinet: 'bg-indigo-50 text-indigo-700',
  exec_announcement: 'bg-yellow-50 text-yellow-700',
};

const REGISTERS = [
  { value: 'first_formal', labelKey: 'sequences.register_first_formal' },
  { value: 'follow_up',    labelKey: 'sequences.register_follow_up' },
  { value: 'introduced',   labelKey: 'sequences.register_introduced' },
  { value: 'warm',         labelKey: 'sequences.register_warm' },
] as const;

type Register = (typeof REGISTERS)[number]['value'];

export function SignalFeedPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [composeSignal, setComposeSignal] = useState<Signal | null>(null);
  const [register, setRegister] = useState<Register>('first_formal');

  const feed = useQuery({
    queryKey: ['signals', wsId],
    queryFn: () => signalsApi.list({ workspaceId: wsId, limit: 100 }),
    enabled: !!wsId,
  });

  const seedStub = useMutation({
    mutationFn: () => signalsApi.ingestStub(wsId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signals', wsId] }),
  });

  const markStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Signal['status'] }) =>
      signalsApi.markStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signals', wsId] }),
  });

  const removeSignal = useMutation({
    mutationFn: (id: string) => signalsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signals', wsId] });
      toast.success(t('signals.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  async function askDeleteSignal(s: Signal) {
    const ok = await confirm({
      title: t('signals.confirm_delete_title'),
      description: t('signals.confirm_delete_body'),
      confirmLabel: t('signals.delete'),
      variant: 'danger',
    });
    if (ok) removeSignal.mutate(s.id);
  }

  // Keigo Composer — fires when a signal's Compose → is clicked. The
  // signal id + (if present) contact id are passed so the agent can
  // build a real outreach draft citing the signal.
  const compose = useMutation({
    mutationFn: () => {
      if (!composeSignal) throw new Error('no signal');
      return agentsApi.keigoCompose({
        workspaceId: wsId,
        // Omit contactId rather than send '' — backend's @IsUUID
        // rejects empty strings. Service falls back to the signal's
        // contact_id / account_id when contactId is undefined.
        contactId: composeSignal.contactId || undefined,
        signalId: composeSignal.id,
        register,
      });
    },
    onError: () => toast.error(t('agents.run_failed')),
  });

  const items = feed.data?.items ?? [];
  const composePromptPreview = composeSignal
    ? `signal:${composeSignal.id} kind:${composeSignal.kind} register:${register}`
    : undefined;

  function openCompose(s: Signal) {
    setComposeSignal(s);
    setRegister('first_formal');
    // Mark the signal as 'acted' so the feed doesn't keep nagging.
    if (s.status === 'new') markStatus.mutate({ id: s.id, status: 'acted' });
  }

  async function copyComposedDraft() {
    const out = compose.data?.output;
    if (!out || typeof out.subject !== 'string' || typeof out.body !== 'string') return;
    try {
      await navigator.clipboard.writeText(`${out.subject}\n\n${out.body}`);
      toast.success(t('signals.compose_copied'));
    } catch {
      // clipboard blocked — silent
    }
  }

  // Attach-to-record: persist the composed keigo draft as a note (comment)
  // on the signal's linked account (preferred) or contact, so the draft is
  // actionable beyond the AI-results history.
  const attachNote = useMutation({
    mutationFn: () => {
      const out = compose.data?.output;
      if (!composeSignal) throw new Error('no signal');
      if (!out || typeof out.subject !== 'string' || typeof out.body !== 'string') {
        throw new Error('no draft');
      }
      const targetKind = composeSignal.accountId ? 'account' : 'contact';
      const targetId = composeSignal.accountId || composeSignal.contactId;
      if (!targetId) throw new Error('no target');
      return commentsApi.create(wsId, {
        targetKind,
        targetId,
        body: `件名: ${out.subject}\n\n${out.body}`,
      });
    },
    onSuccess: () => toast.success(t('signals.compose_note_saved')),
    onError: (e) =>
      toast.error(
        e instanceof Error && e.message === 'no target'
          ? t('signals.compose_no_target')
          : t('common.save_failed'),
      ),
  });

  // Signal → sequence loop (NUMBER_ONE_PLAN W3): a hot signal with a linked
  // contact can be turned into outreach in one click by enrolling that
  // contact into a sequence. Closes the feed → sequences → send loop that
  // previously required the user to leave the feed, find the contact, and
  // enroll manually.
  const [enrollSignal, setEnrollSignal] = useState<Signal | null>(null);
  const sequencesQ = useQuery({
    queryKey: ['sequences', wsId],
    queryFn: () => sequencesApi.list(wsId),
    enabled: !!wsId,
  });
  const activeSequences = (sequencesQ.data ?? []).filter((s) => s.active);

  const enroll = useMutation({
    mutationFn: (sequenceId: string) => {
      if (!enrollSignal?.contactId) throw new Error('no contact');
      return sequencesApi.enroll(sequenceId, enrollSignal.contactId);
    },
    onSuccess: () => {
      if (enrollSignal && enrollSignal.status !== 'acted') {
        markStatus.mutate({ id: enrollSignal.id, status: 'acted' });
      }
      setEnrollSignal(null);
      toast.success(t('signals.enrolled_toast'));
    },
    onError: () => toast.error(t('signals.enroll_failed')),
  });

  return (
    <AppShell titleKey="signals.title">
      <SampleDataBanner wsId={wsId} feedEmpty={!feed.isLoading && items.length === 0} />
      <NextBestAction wsId={wsId} />
      <WeeklyBriefing wsId={wsId} />
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-ink-600 max-w-2xl">{t('signals.intro')}</p>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <ShareSnapshotButton
              workspaceId={wsId}
              kind="signal_feed"
              payload={{
                generatedAt: new Date().toISOString(),
                count: items.length,
                items: items.slice(0, 20).map((s) => ({
                  id: s.id,
                  kind: s.kind,
                  occurredAt: s.occurredAt,
                  score: s.score,
                  payload: s.payload,
                })),
              }}
              label={`Signal feed · ${new Date().toISOString().slice(0, 10)}`}
            />
          )}
          <button
            onClick={() => seedStub.mutate()}
            disabled={seedStub.isPending}
            className="text-xs px-3 py-1.5 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
          >
            {seedStub.isPending ? t('signals.seeding') : t('signals.seed_button')}
          </button>
        </div>
      </div>

      {feed.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {feed.isError && <ErrorState onRetry={() => feed.refetch()} />}
      {!feed.isLoading && !feed.isError && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500">
          <p className="text-sm">{t('signals.empty_title')}</p>
          <p className="text-xs mt-2">{t('signals.empty_sub')}</p>
        </div>
      )}

      <ul className="space-y-2">
        {items.map((s) => {
          const kindLabel = t(KIND_KIND[s.kind] || 'signals.kind_press');
          const color = KIND_COLOR[s.kind] || 'bg-ink-100 text-ink-700';
          const title = String(s.payload?.title ?? kindLabel);
          const subtitle = String(s.payload?.subtitle ?? s.source);
          return (
            <li key={s.id} className="rounded-xl border border-ink-200 bg-white p-4 flex items-start gap-4">
              <span className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-2 py-0.5 ${color}`}>
                {kindLabel}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-900 truncate">{title}</div>
                <div className="text-xs text-ink-500 truncate">
                  {subtitle} · {formatLocaleDate(s.occurredAt, i18n.language, { withTime: true })} · {t('signals.score_label')}{' '}
                  <span className="num">{s.score ?? 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {s.status === 'new' && (
                  <button
                    onClick={() => markStatus.mutate({ id: s.id, status: 'read' })}
                    className="text-xs px-2.5 py-1 rounded-md border border-ink-200 text-ink-700 hover:bg-ink-100"
                  >
                    {t('signals.mark_read')}
                  </button>
                )}
                <button
                  onClick={() => openCompose(s)}
                  className="text-xs px-2.5 py-1 rounded-md bg-accent-500 text-white hover:bg-accent-600"
                >
                  {t('signals.compose')}
                </button>
                {s.contactId && (
                  <button
                    onClick={() => setEnrollSignal(s)}
                    className="text-xs px-2.5 py-1 rounded-md border border-accent-200 text-accent-700 hover:bg-accent-50"
                  >
                    {t('signals.enroll')}
                  </button>
                )}
                <button
                  onClick={() => askDeleteSignal(s)}
                  aria-label={t('signals.delete')}
                  title={t('signals.delete')}
                  className="rounded-md p-1.5 text-ink-400 hover:bg-warn-500/10 hover:text-warn-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Compose modal — opens the keigo composer pre-loaded with the
          tapped signal. The agent reads the signal payload via the
          lookup_signal tool and writes a real outreach draft, cited. */}
      <Modal
        open={!!composeSignal}
        onOpenChange={(open) => {
          if (!open) {
            setComposeSignal(null);
            compose.reset();
          }
        }}
        size="lg"
        title={t('signals.compose_modal_title')}
      >
        {composeSignal && (
          <div className="space-y-4">
            <div className="rounded-md border border-ink-200 bg-ink-50 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-1">
                {t('signals.compose_modal_signal')}
              </p>
              <p className="text-sm text-ink-900 font-medium">
                {String(composeSignal.payload?.title ?? t(KIND_KIND[composeSignal.kind]))}
              </p>
              <p className="text-xs text-ink-500 mt-0.5">
                {t(KIND_KIND[composeSignal.kind])} ·{' '}
                {formatLocaleDate(composeSignal.occurredAt, i18n.language, { withTime: true })}
              </p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-ink-500 font-semibold">
                {t('signals.compose_modal_register')}
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {REGISTERS.map((r) => {
                  const active = register === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRegister(r.value)}
                      className={
                        'text-left px-3 py-2 rounded-md border text-sm transition ' +
                        (active
                          ? 'border-accent-500 bg-accent-50 text-accent-700 font-semibold'
                          : 'border-ink-300 hover:bg-ink-100')
                      }
                    >
                      {t(r.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            {!compose.data && !compose.isPending && (
              <button
                onClick={() => compose.mutate()}
                disabled={compose.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md bg-ink-900 text-white text-sm font-semibold hover:bg-ink-800"
              >
                {t('signals.compose_modal_generate')}
              </button>
            )}

            {(compose.data || compose.isPending) && (
              <AgentRunPanel
                response={compose.data}
                prompt={composePromptPreview}
                isPending={compose.isPending}
              />
            )}

            {compose.data?.output?.subject && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={copyComposedDraft}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-ink-300 hover:bg-ink-100"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t('signals.compose_modal_copy')}
                </button>
                <button
                  type="button"
                  onClick={() => attachNote.mutate()}
                  disabled={attachNote.isPending}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-ink-300 hover:bg-ink-100 disabled:opacity-60"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {t('signals.compose_save_note')}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Enroll modal — pick an active sequence to enrol the signal's
          contact into. The signal→sequence loop (W3): turn a buying signal
          into outreach without leaving the feed. */}
      <Modal
        open={!!enrollSignal}
        onOpenChange={(open) => {
          if (!open) {
            setEnrollSignal(null);
            enroll.reset();
          }
        }}
        title={t('signals.enroll_modal_title')}
      >
        {enrollSignal && (
          <div className="space-y-4">
            <div className="rounded-md border border-ink-200 bg-ink-50 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-1">
                {t('signals.compose_modal_signal')}
              </p>
              <p className="text-sm text-ink-900 font-medium">
                {String(enrollSignal.payload?.title ?? t(KIND_KIND[enrollSignal.kind]))}
              </p>
            </div>

            {sequencesQ.isLoading && (
              <p className="text-sm text-ink-500">{t('common.loading')}</p>
            )}
            {!sequencesQ.isLoading && activeSequences.length === 0 && (
              <p className="text-sm text-ink-500">{t('signals.enroll_no_sequences')}</p>
            )}
            {activeSequences.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-ink-700">{t('signals.enroll_pick')}</p>
                <ul className="space-y-1.5">
                  {activeSequences.map((seq) => (
                    <li key={seq.id}>
                      <button
                        onClick={() => enroll.mutate(seq.id)}
                        disabled={enroll.isPending}
                        className="w-full flex items-center justify-between gap-3 rounded-lg border border-ink-200 px-3.5 py-2.5 text-left hover:border-accent-300 hover:bg-accent-50/40 transition disabled:opacity-60"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink-900 truncate">
                            {seq.name}
                          </span>
                          <span className="block text-xs text-ink-500">
                            {t('signals.enroll_steps', { count: seq.steps?.length ?? 0 })}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-semibold text-accent-700">
                          {enroll.isPending ? t('common.loading') : t('signals.enroll')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
